import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import difference from 'lodash/difference';

import { FeatureFlag, GUID, Role } from '@ndvr/auth-api';
import {
    Calendar,
    CalendarEventSyncView,
    CommandCenterHousehold,
    GlobalAccount,
    NoteTemplateType,
    NoteView,
    PersonOrg,
    Tenant,
} from '@ndvr/customer-api';
import { AccountGroup } from '@ndvr/portfolio-api';

import { AuthApi, CustomerApi, NcrmApi, PortfolioApi } from 'API';
import { formatDate } from 'utils';
import { Toast } from 'widgets';

import { type FeatureFlagWithOverrides } from './featureFlag/FeatureFlagList';

export const useFeatureFlags = () =>
    useQuery({
        queryKey: ['featureFlag', 'list'],
        queryFn: async () => {
            const [response] = await AuthApi().getFeatureFlags();
            return response ?? undefined;
        },
    });

export const useFeatureFlagOverrides = () =>
    useQuery({
        queryKey: ['featureFlagOverride', 'list'],
        queryFn: async () => {
            const [response] = await AuthApi().getFeatureFlagOverrides();
            return response ?? undefined;
        },
    });

export const useToggleFeatureFlag = (featureFlag: FeatureFlag) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ personOrgGUID, isEnabled }: { personOrgGUID?: GUID<PersonOrg>; isEnabled: boolean }) => {
            return isEnabled
                ? AuthApi().setFeatureFlagOverride(featureFlag, { personOrgGUID })
                : AuthApi().removeFeatureFlagOverride(featureFlag, { personOrgGUID });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['featureFlagOverride', 'list'],
            });
            queryClient.invalidateQueries({
                queryKey: ['person', 'featureFlags'],
            });
        },
    });
};

export const useUpdateFeatureFlagOverrides = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            updated,
            original,
        }: {
            updated: FeatureFlagWithOverrides;
            original: FeatureFlagWithOverrides;
        }) => {
            const ops = [
                ...difference(updated.overrides, original.overrides).map((override) =>
                    AuthApi().setFeatureFlagOverride(updated.featureFlag, {
                        personOrgGUID: override,
                    }),
                ),
                ...difference(original.overrides, updated.overrides).map((override) =>
                    AuthApi().removeFeatureFlagOverride(updated.featureFlag, {
                        personOrgGUID: override,
                    }),
                ),
            ];

            if (updated.globalOverride !== original.globalOverride) {
                ops.push(
                    updated.globalOverride
                        ? AuthApi().setFeatureFlagOverride(updated.featureFlag)
                        : AuthApi().removeFeatureFlagOverride(updated.featureFlag),
                );
            }

            await Promise.all(ops);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['featureFlagOverride', 'list'],
            });
        },
    });
};

export const useJournalEvents = (startDate?: Date, endDate?: Date) =>
    useQuery({
        queryKey: ['journal', 'list', { startDate, endDate }],
        queryFn: async () => {
            const [response] = await NcrmApi().getJournalEvents({
                fromInclusive: formatDate(startDate, 'SERVER'),
                toInclusive: formatDate(endDate, 'SERVER'),
            });
            return response ?? undefined;
        },
    });

export const useUserHasRole = (role: Role) =>
    useQuery({
        queryKey: ['person', 'role', role],
        queryFn: async () => {
            const [response] = await AuthApi().hasRole(role);
            return response || false;
        },
    });

export const useTriggerBoxIntake = () => {
    return useMutation({
        mutationFn: () => {
            return CustomerApi().processTaxIntakeFolder();
        },
    });
};

export const useTriggerHouseholdOnboarding = () => {
    return useMutation({
        mutationFn: async ({ tenantGUID, primaryAdvisorGUID }: { tenantGUID: string; primaryAdvisorGUID: string }) => {
            await CustomerApi().processHouseholdOnboarding(tenantGUID, primaryAdvisorGUID);
        },
        onSuccess: () => {
            Toast.success('Household onboarding completed successfully');
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to trigger household onboarding');
        },
    });
};

export const useHouseholdsForTenant = (tenantGUID?: GUID<Tenant>) => {
    return useQuery<CommandCenterHousehold[]>({
        queryKey: ['tenant', tenantGUID, 'global-accounts', 'cc-list'],
        queryFn: async () => {
            const [response] = await CustomerApi().getCommandCenterHouseholdList(tenantGUID!);
            return response ?? [];
        },
        enabled: !!tenantGUID,
    });
};

export const useOnboardSingleAccountFromHouseholdOnboarding = () => {
    return useMutation({
        mutationFn: async ({
            accountNumber,
            householdGUID,
        }: {
            accountNumber: string;
            householdGUID: GUID<GlobalAccount>;
        }) => {
            await CustomerApi().onboardAccountFromHouseholdOnboarding(accountNumber, householdGUID);
        },
        onSuccess: () => {
            Toast.success('Account onboarding completed successfully');
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to onboard account');
        },
    });
};

export const useTriggerProspectOnboarding = () => {
    return useMutation({
        mutationFn: async ({ tenantGUID, primaryAdvisorGUID }: { tenantGUID: string; primaryAdvisorGUID: string }) => {
            await CustomerApi().processProspectOnboarding(tenantGUID, primaryAdvisorGUID);
        },
        onSuccess: () => {
            Toast.success('Prospect onboarding completed successfully');
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to trigger prospect onboarding');
        },
    });
};

export const useImportRedtailData = () => {
    return useMutation({
        mutationFn: async ({ tenantGUID, advisorGUID }: { tenantGUID: string; advisorGUID: string }) => {
            await CustomerApi().importForAdvisor(tenantGUID, advisorGUID);
        },
        onSuccess: () => {
            Toast.success('Redtail import completed successfully');
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to import Redtail data');
        },
    });
};

export const useMigrateEmails = () => {
    return useMutation({
        mutationFn: async ({ tenantGUID }: { tenantGUID: string }) => {
            const [count] = await CustomerApi().migrateEmails({ tenantGUID });
            return count;
        },
        onSuccess: (count) => {
            Toast.success(`Email migration completed. ${count} emails migrated.`);
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to migrate emails');
        },
    });
};

export const useMigrateNotes = () => {
    return useMutation({
        mutationFn: async ({ tenantGUID }: { tenantGUID: string }) => {
            const [count] = await CustomerApi().migrateNotes({ tenantGUID });
            return count;
        },
        onSuccess: (count) => {
            Toast.success(`Note migration completed. ${count} notes migrated.`);
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to migrate notes');
        },
    });
};

export const useMigrateTextMessages = () => {
    return useMutation({
        mutationFn: async () => {
            const [count] = await CustomerApi().migrateTextMessages();
            return count;
        },
        onSuccess: (count) => {
            Toast.success(`Text message migration completed. ${count} text messages migrated.`);
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to migrate text messages');
        },
    });
};

export const useEmbedDocument = () => {
    return useMutation({
        mutationFn: async ({ entityType, entityGUID }: { entityType: string; entityGUID: string }) => {
            await CustomerApi().embed({ entityType, entityGUID });
        },
        onSuccess: () => {
            Toast.success('Document embedded successfully');
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to embed document');
        },
    });
};

export const useStartIngestion = () => {
    return useMutation({
        mutationFn: async () => {
            await CustomerApi().startIngestion();
        },
        onSuccess: () => {
            Toast.success('Ingestion started successfully');
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to start ingestion');
        },
    });
};

export const useRedtailExportTenant = () => {
    return useMutation({
        mutationFn: async ({ tenantGUID }: { tenantGUID: GUID<Tenant> }) => {
            const [result] = await CustomerApi().exportTenant(tenantGUID);
            return result;
        },
        onSuccess: (result) => {
            if (!result) return;
            const total =
                result.contactsExported +
                result.addressesExported +
                result.emailsExported +
                result.phonesExported +
                result.familyLinksExported +
                result.accountsExported +
                result.notesExported +
                result.tasksExported +
                result.opportunitiesExported;
            const errorCount = result.errors?.length ?? 0;
            const msg = `Redtail export completed. ${total} records exported${errorCount > 0 ? `, ${errorCount} error(s)` : ''}.`;
            errorCount > 0 ? Toast.info(msg) : Toast.success(msg);
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to export to Redtail');
        },
    });
};

export const useListCalendars = () =>
    useQuery({
        queryKey: ['calendar', 'list'],
        queryFn: async () => {
            const [response] = await CustomerApi().listCalendars();
            return response ?? [];
        },
    });

export const useSyncCalendar = () =>
    useMutation({
        mutationFn: async (calendarGUID: GUID<Calendar>) => {
            await CustomerApi().syncCalendar(calendarGUID);
        },
        onSuccess: () => {
            Toast.success('Calendar sync triggered.');
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to sync calendar.');
        },
    });

export const useSyncCalendarEvent = () =>
    useMutation({
        mutationFn: async ({ grantId, nylasEventId }: { grantId: string; nylasEventId: string }) => {
            const [response] = await CustomerApi().syncCalendarEvent({ grantId, nylasEventId });
            return response as CalendarEventSyncView;
        },
        onSuccess: (data) => {
            Toast.success(`Event synced. ${data.syncedOccurrenceCount} occurrence(s) updated.`);
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to sync calendar event.');
        },
    });

export const useMoveAccountGroupAcrossHouseholds = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            accountGroupGUID,
            targetHouseholdGUID,
        }: {
            accountGroupGUID: GUID<AccountGroup>;
            targetHouseholdGUID: GUID<GlobalAccount>;
        }) => {
            await PortfolioApi().moveAccountGroupAcrossHouseholds(accountGroupGUID, targetHouseholdGUID);
        },
        onSuccess: (_data, variables) => {
            Toast.success(`Moved account group ${variables.accountGroupGUID}.`);
            queryClient.invalidateQueries({ queryKey: ['globalAccount'] });
            queryClient.invalidateQueries({ queryKey: ['accountGroup'] });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to move account group.');
        },
    });
};

export const useGenerateWritingStyle = () => {
    return useMutation({
        mutationFn: async (internalUserProfileGUID: string) => {
            const [response] = await CustomerApi().generateWritingStyle(internalUserProfileGUID);
            return response;
        },
        onSuccess: () => {
            Toast.success('Writing style generated successfully');
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to generate writing style');
        },
    });
};

export const useRegenerateMeetingNote = () => {
    return useMutation({
        mutationFn: async ({
            noteGUID,
            noteTemplateType,
        }: {
            noteGUID: GUID<NoteView>;
            noteTemplateType: NoteTemplateType;
        }) => {
            await CustomerApi().applyTemplateToNote(noteGUID, { noteTemplateType });
        },
        onSuccess: () => {
            Toast.success('Meeting note regenerated from the stored transcript.');
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to regenerate meeting note');
        },
    });
};
